"use client";

import { DateTime } from "luxon";

import {
  getHeartplaceReferralReasonText,
  isHeartplaceFortWorthFaxNumber,
} from "@/configuration/groups/ushv-heartplace/configuration";
import { isPassthroughAssignee } from "@/configuration/groups/ushv-heartplace/passthrough-assignee";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { ReferralSchedulingRecommendation } from "@/src/components/comms/referral-scheduling-recommendation";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/src/components/shadcn/ui/table";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { useFaxLocationAssignment } from "@/src/lib/data-fetchers/location-assignment-fetcher";
import { usePatientAppointments } from "@/src/lib/data-fetchers/scheduling-fetchers";
import { TaskSource } from "@/src/lib/task-source";
import { formatDateForFilename, formatDateTimeWithTimezoneForDisplay } from "@/src/lib/utils/date-and-phone-utils";
import { isStatOrUrgentPriority, Referral, SchedulingPriority } from "@/src/models/documents/referral";
import { getProviderNameWithCredentials } from "@/src/models/practitioner";
import { Task } from "@/src/models/task";

const HEARTPLACE_DISPLAY_TIMEZONE = "America/Chicago";

function getDiagnosisList(referral: Referral): string[] | null {
  if (referral.diagnosisList && referral.diagnosisList.length > 0) {
    const isNonEmpty = (text: string | null): text is string => !!text;

    return referral.diagnosisList
      .map((d: { diagnosisText: string | null; diagnosisCode: string | null }) => d.diagnosisText)
      .filter(isNonEmpty);
  }

  if (referral.reasonForReferral) {
    return [referral.reasonForReferral];
  }

  return null;
}

function getSchedulingPriorityText(referral: Referral): string {
  const priority = referral.schedulingPriority || SchedulingPriority.ROUTINE;

  if (isStatOrUrgentPriority(priority)) {
    return `${priority} → If stat or urgent, change priority`;
  }

  return priority;
}

function HeartplaceReferralInstructionsContent({
  referral,
  task,
  schedulingVerificationTaskEnabled,
}: {
  referral: Referral;
  task: Task;
  schedulingVerificationTaskEnabled: boolean;
}) {
  const { referralLocation, authLocation, staffLocation, faxRecipientNumber } = useFaxLocationAssignment(task.id);
  const { appointments, isPending: isAppointmentsPending } = usePatientAppointments(referral.patient?.id);

  const startOfDay = DateTime.now().setZone(HEARTPLACE_DISPLAY_TIMEZONE).startOf("day");
  const upcomingAppointments = appointments?.filter((appointment) => {
    const appointmentDate = DateTime.fromISO(appointment.appointmentDatetime || "").setZone(
      HEARTPLACE_DISPLAY_TIMEZONE
    );

    return appointmentDate.isValid && appointmentDate >= startOfDay;
  });

  const displayLocation = staffLocation || referralLocation;
  // Mirrors the REFERRAL documentLabel in HEARTPLACE_MAPPINGS.
  const fileName = `${formatDateForFilename(referral.dateOfReferral)} Referral Order`;
  const isFortWorth = isHeartplaceFortWorthFaxNumber(faxRecipientNumber);
  const prefix = isFortWorth ? "FWH" : "HP";
  const assignToReferrals = referralLocation
    ? isPassthroughAssignee(referralLocation)
      ? referralLocation
      : `${prefix} ${referralLocation}, Referrals`
    : `${prefix} Referrals`;
  const facilityName = displayLocation ? `Heartplace ${displayLocation} OF` : "Heartplace OF";
  const diagnosisList = getDiagnosisList(referral);
  const isManualUpload = task.source === TaskSource.UPLOAD_DOCUMENT;
  const baseReferralReason = getHeartplaceReferralReasonText(referral, isFortWorth);
  const referralReason = isManualUpload
    ? `${baseReferralReason.endsWith(".") ? baseReferralReason.slice(0, -1) : baseReferralReason}. LeadingReach Referral`
    : baseReferralReason;
  const schedulingPriority = getSchedulingPriorityText(referral);
  const referringProviderName = getProviderNameWithCredentials(referral.referringProvider);

  return (
    <div className="flex flex-col gap-4">
      <span className="font-medium text-center">Heartplace Referral Instructions:</span>
      <div className="text-muted-foreground space-y-4">
        {/* No Upcoming Appointment */}
        <div className="flex flex-col gap-2">
          <span className="font-semibold text-foreground">No Upcoming Appointment</span>
          <div className="flex flex-col gap-3 mt-1">
            <div>
              <span className="font-semibold">1. Upload the file to the Referral Documents folder</span>
              <div className="ml-4 mt-1 space-y-1">
                <div>
                  <span className="font-medium">File name:</span> <CopyableContent text={fileName} />
                </div>
                <div>
                  <span className="font-medium">Mark document reviewed</span>
                </div>
              </div>
            </div>
            <div>
              <span className="font-semibold">2. Create a referral order</span>
              <div className="ml-4 mt-1 space-y-1">
                <div>
                  <span className="font-medium">Assign to:</span> <CopyableContent text={assignToReferrals} />
                </div>
                <div>
                  <span className="font-medium">Facility:</span> <CopyableContent text={facilityName} />
                </div>
                <div>
                  <span className="font-medium">Reason:</span> <CopyableContent text={referralReason} />
                </div>
                <div>
                  <span className="font-medium">Diagnosis:</span>
                  {diagnosisList && diagnosisList.length > 0 ? (
                    <div className="ml-4 space-y-1 mt-1">
                      {diagnosisList.map((diagnosis, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <span className="text-muted-foreground">•</span>
                          <CopyableContent text={diagnosis} />
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div>
                  <span className="font-medium">Scheduling Priority:</span>{" "}
                  <CopyableContent text={schedulingPriority} />
                </div>
                <div>
                  <span className="font-medium">Referring provider:</span>{" "}
                  <CopyableContent text={referringProviderName} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Upcoming Appointment w/in 1 month */}
        <div className="flex flex-col gap-2">
          <span className="font-semibold text-foreground">
            Upcoming Appointment w/in 1 month → do not create a referral order. Instead:
          </span>
          <div>
            <span className="font-semibold">Upload document to patient chart</span>
            <div className="ml-4 mt-1 space-y-1">
              <div>
                <span className="font-medium">Assign to:</span> <CopyableContent text={assignToReferrals} />
              </div>
              <div>
                <span className="font-medium">File name:</span> <CopyableContent text={fileName} />
              </div>
              <div>
                <span className="font-medium">Mark document reviewed</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Location + scheduling */}
      <div className="space-y-1">
        <div>
          <span className="font-semibold text-foreground">Referral Location:</span>{" "}
          <CopyableContent text={displayLocation || "N/A"} />
        </div>
        <div>
          <span className="font-semibold text-foreground">Auth Location:</span>{" "}
          <CopyableContent text={authLocation || "N/A"} />
        </div>
        {!schedulingVerificationTaskEnabled && (
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-foreground">Scheduling recommendation:</span>
            <ReferralSchedulingRecommendation referral={referral} title="" />
          </div>
        )}
      </div>

      {/* Future Encounters */}
      <div>
        <span className="font-semibold text-foreground">Future Encounters:</span>
        {isAppointmentsPending ? (
          <span className="ml-2 text-xs text-muted-foreground">Checking for upcoming encounters...</span>
        ) : upcomingAppointments?.length ? (
          <Table className="mt-2 text-xs">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {upcomingAppointments.map((appointment) => (
                <TableRow key={appointment.id}>
                  <TableCell>
                    {formatDateTimeWithTimezoneForDisplay(
                      appointment.appointmentDatetime,
                      HEARTPLACE_DISPLAY_TIMEZONE,
                      false
                    )}
                  </TableCell>
                  <TableCell>{appointment.ehrType || "N/A"}</TableCell>
                  <TableCell>{appointment.ehrReason || "N/A"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <span className="ml-2 text-xs text-muted-foreground">No upcoming encounters found</span>
        )}
      </div>
    </div>
  );
}

function HeartplaceReferralCards({ referral }: { referral: Referral }) {
  return (
    <>
      <PatientCard patient={referral.patient} />
      <ReferralProviderCards referral={referral} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage!} type="Primary" isEditable />}
      {referral.secondaryCoverage && (
        <CoverageCard coverage={referral.secondaryCoverage!} type="Secondary" isEditable />
      )}
    </>
  );
}

interface HeartplaceReferralInstructionsProps {
  referral: Referral;
  task: Task;
  schedulingVerificationTaskEnabled: boolean;
}

export function HeartplaceReferralInstructions({
  referral,
  task,
  schedulingVerificationTaskEnabled,
}: HeartplaceReferralInstructionsProps) {
  return (
    <div className="flex flex-col gap-8 w-full sm:min-w-[800px] max-w-[1200px] mx-auto">
      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <HeartplaceReferralInstructionsContent
            referral={referral}
            task={task}
            schedulingVerificationTaskEnabled={schedulingVerificationTaskEnabled}
          />
        </div>
      </EhrInstructionsCard>

      <HeartplaceReferralCards referral={referral} />
    </div>
  );
}
